import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../services/api";
import { DEFAULT_OFFERS_PERIOD } from "../constants/dashboardPeriods";
import { DASHBOARD_LIST_PAGE_SIZE } from "../constants/dashboardListPagination";
import { fetchOwnerOffers } from "../services/ownerPatrimonyService";
import {
  DashboardPeriodPreset,
  IOffer,
  IOwnerKenloEndpoints,
  IOwnerOffersFilters,
  IPaginationMeta,
  OfferStatusFilter,
  OwnerPatrimonyRequestStatus,
} from "../types/ownerDashboard.types";
import { OwnerOffersQuery } from "../utils/buildOwnerOffersParams";
import { filterOffersByQuery, paginateOffersLocally } from "../utils/filterOffersByQuery";

function matchesOfferStatusForFilter(status: string, statusFilter?: OfferStatusFilter): boolean {
  if (!statusFilter) {
    return true;
  }

  if (statusFilter === "pending") {
    return status === "CONTESTED" || status === "PENDING";
  }

  return status === "ACCEPT" || status === "ACCEPTED";
}

interface UseOwnerOffersOptions {
  accessToken: string | null;
  enabled: boolean;
  endpoints?: IOwnerKenloEndpoints;
  historyYears?: number;
  fallbackOffers?: IOffer[];
}

interface UseOwnerOffersResult {
  offers: IOffer[];
  filters: IOwnerOffersFilters | null;
  pagination: IPaginationMeta | null;
  status: OwnerPatrimonyRequestStatus;
  query: OwnerOffersQuery;
  setPeriodPreset: (period: DashboardPeriodPreset) => Promise<void>;
  setPropertyId: (propertyId: number | null) => Promise<void>;
  setStatusFilter: (statusFilter: OfferStatusFilter) => Promise<void>;
  setPage: (page: number) => Promise<void>;
  retry: () => Promise<void>;
}

function createDefaultOffersQuery(): OwnerOffersQuery {
  return {
    periodPreset: DEFAULT_OFFERS_PERIOD,
    propertyId: null,
    statusFilter: "pending",
    page: 1,
    perPage: DASHBOARD_LIST_PAGE_SIZE,
  };
}

function resolveOffersFromFallback(
  requestQuery: OwnerOffersQuery,
  fallbackOffers: IOffer[],
  historyYears?: number
): { items: IOffer[]; pagination: IPaginationMeta } {
  const filteredOffers = filterOffersByQuery(fallbackOffers, {
    periodPreset: requestQuery.periodPreset,
    propertyId: requestQuery.propertyId,
    statusFilter: requestQuery.statusFilter,
    historyYears,
  });

  const paginated = paginateOffersLocally(
    filteredOffers,
    requestQuery.page ?? 1,
    requestQuery.perPage ?? DASHBOARD_LIST_PAGE_SIZE
  );

  return {
    items: paginated.items,
    pagination: paginated.pagination,
  };
}

export function useOwnerOffers({
  accessToken,
  enabled,
  endpoints,
  historyYears,
  fallbackOffers = [],
}: UseOwnerOffersOptions): UseOwnerOffersResult {
  const [offers, setOffers] = useState<IOffer[]>([]);
  const [filters, setFilters] = useState<IOwnerOffersFilters | null>(null);
  const [pagination, setPagination] = useState<IPaginationMeta | null>(null);
  const [status, setStatus] = useState<OwnerPatrimonyRequestStatus>("idle");
  const [query, setQuery] = useState<OwnerOffersQuery>(createDefaultOffersQuery);
  const queryRef = useRef(query);
  const historyYearsRef = useRef(historyYears);
  const fallbackOffersRef = useRef(fallbackOffers);
  const latestRequestIdRef = useRef(0);
  const hasDeferredLoadStarted = useRef(false);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    historyYearsRef.current = historyYears;
  }, [historyYears]);

  useEffect(() => {
    fallbackOffersRef.current = fallbackOffers;
  }, [fallbackOffers]);

  const loadOffers = useCallback(
    async (requestQuery: OwnerOffersQuery) => {
      if (!enabled || !accessToken) {
        return;
      }

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;

      setQuery(requestQuery);
      setStatus("loading");

      const resolvedHistoryYears = requestQuery.historyYears ?? historyYearsRef.current;
      const resolvedQuery =
        requestQuery.periodPreset === "all_time"
          ? {
              ...requestQuery,
              historyYears: resolvedHistoryYears,
            }
          : requestQuery;

      try {
        api.defaults.headers.authorization = `Bearer ${accessToken}`;
        const response = await fetchOwnerOffers(resolvedQuery, endpoints);

        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        const hasUnfilteredStatuses =
          !!requestQuery.statusFilter &&
          response.items.some(
            (offer) => !matchesOfferStatusForFilter(offer.status, requestQuery.statusFilter)
          );

        if (
          (response.items.length === 0 || hasUnfilteredStatuses) &&
          fallbackOffersRef.current.length > 0
        ) {
          const fallbackResult = resolveOffersFromFallback(
            requestQuery,
            fallbackOffersRef.current,
            resolvedHistoryYears
          );

          if (fallbackResult.pagination.total > 0) {
            setOffers(fallbackResult.items);
            setFilters(response.filters);
            setPagination(fallbackResult.pagination);
            setStatus("success");
            return;
          }
        }

        setOffers(response.items);
        setFilters(response.filters);
        setPagination(response.pagination);
        setStatus("success");
      } catch {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }

        if (fallbackOffersRef.current.length > 0) {
          const fallbackResult = resolveOffersFromFallback(
            requestQuery,
            fallbackOffersRef.current,
            resolvedHistoryYears
          );

          setOffers(fallbackResult.items);
          setFilters(null);
          setPagination(fallbackResult.pagination);
          setStatus("success");
          return;
        }

        setOffers([]);
        setFilters(null);
        setPagination(null);
        setStatus("error");
      }
    },
    [accessToken, enabled, endpoints]
  );

  const setPeriodPreset = useCallback(
    async (periodPreset: DashboardPeriodPreset) => {
      await loadOffers({
        ...queryRef.current,
        periodPreset,
        customPeriod: null,
        page: 1,
      });
    },
    [loadOffers]
  );

  const setPropertyId = useCallback(
    async (propertyId: number | null) => {
      await loadOffers({
        ...queryRef.current,
        propertyId,
        page: 1,
      });
    },
    [loadOffers]
  );

  const setStatusFilter = useCallback(
    async (statusFilter: OfferStatusFilter) => {
      await loadOffers({
        ...queryRef.current,
        statusFilter,
        page: 1,
      });
    },
    [loadOffers]
  );

  const setPage = useCallback(
    async (page: number) => {
      await loadOffers({
        ...queryRef.current,
        page,
      });
    },
    [loadOffers]
  );

  const retry = useCallback(async () => {
    await loadOffers(query);
  }, [loadOffers, query]);

  useEffect(() => {
    if (!enabled || !accessToken) {
      hasDeferredLoadStarted.current = false;
      return;
    }

    if (hasDeferredLoadStarted.current) {
      return;
    }

    hasDeferredLoadStarted.current = true;

    const backgroundTimer = window.setTimeout(() => {
      loadOffers(createDefaultOffersQuery());
    }, 0);

    return () => {
      window.clearTimeout(backgroundTimer);
    };
  }, [accessToken, enabled, loadOffers]);

  useEffect(() => {
    if (!enabled || !accessToken || fallbackOffers.length === 0) {
      return;
    }

    if (!hasDeferredLoadStarted.current) {
      return;
    }

    loadOffers(queryRef.current);
  }, [accessToken, enabled, fallbackOffers, loadOffers]);

  return {
    offers,
    filters,
    pagination,
    status,
    query,
    setPeriodPreset,
    setPropertyId,
    setStatusFilter,
    setPage,
    retry,
  };
}
